import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — RiskBar. Thin determinate risk track (.riskbar /
 * .riskbar__fill / .risk-* in the showcase app.css). 8px muted pill track,
 * color-variant fill driven by an inline width %: low (green), med (yellow),
 * high (red). No text, no colored borders.
 */
const riskFillVariants = cva('h-full rounded-full', {
  variants: {
    variant: {
      low: 'bg-sober-green-fg',
      med: 'bg-sober-yellow-fg',
      high: 'bg-sober-red',
    },
  },
  defaultVariants: { variant: 'low' },
});

export interface RiskBarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof riskFillVariants> {
  value: number;
  variant: NonNullable<VariantProps<typeof riskFillVariants>['variant']>;
}

export const RiskBar = React.forwardRef<HTMLDivElement, RiskBarProps>(
  ({ className, value, variant, ...props }, ref) => {
    const pct = Math.min(100, Math.max(0, value));
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className={cn('h-2 w-full overflow-hidden rounded-full bg-sober-muted', className)}
        {...props}
      >
        <div className={cn(riskFillVariants({ variant }))} style={{ width: `${pct}%` }} />
      </div>
    );
  }
);
RiskBar.displayName = 'RiskBar';

export { riskFillVariants };
